// ProductList.js
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Product from "../Components/productListcomponents/Product";
import ProductSidebar from "../Components/productListcomponents/ProductSidebar";
import { showErrorToast } from "../utils/toaster";
import ProductcategoryDrawer from "../Components/productListcomponents/ProductcategoryDrawer";
import { baseurl } from "../config/url";
import { useNavigate } from "react-router-dom";
import { FaHome } from "react-icons/fa";

const ProductList = () => {
  const { subcategoryId } = useParams();
  const location = useLocation();
  const { categoryname } = location.state || {};

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(30);
  const [priceRange, setPriceRange] = useState([0, 1000000000000]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sizeoptions, setsizeoptions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const navigate = useNavigate();

  const priceOptions = [
    { label: "0 to 500", range: [0, 500] },
    { label: "500 to 1500", range: [500, 1500] },
    { label: "1500 to 3000", range: [1500, 3000] },
    { label: "3000 to 5000", range: [3000, 5000] },
    { label: "5000 and above", range: [5000, 1000000] },
  ];

  const fetchProducts = async () => {
    setProducts([]);
    try {
      const response = await axios.get(
        `${baseurl}/api/admin/subcategory/${subcategoryId}?limit=30`
      );
      const data = response?.data || [];
      setProducts(data);
      setFilteredProducts(data);

      const sizearray = data.map((p) => p.size);
      setsizeoptions(sizearray);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again later.";
      showErrorToast(errorMessage);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${baseurl}/api/product`);
      const allData = response?.data?.products || [];
      setAllProducts(allData);
    } catch (error) {
      console.error("Error fetching all products:", error.message);
      setAllProducts([]);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchAllProducts();
  }, [subcategoryId]);

  useEffect(() => {
    const filtered = products.filter((product) => {
      const matchesPrice =
        product.price >= priceRange[0] && product.price <= priceRange[1];
      const matchesSize =
        selectedSizes.length === 0 || selectedSizes.includes(product.size);
      return matchesPrice && matchesSize;
    });
    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [priceRange, selectedSizes, products]);

  const handleResetFilters = () => {
    setPriceRange([0, 1000000000000]);
    setSelectedSizes([]);
  };

  const handleSizeChange = (size) => {
    setSelectedSizes((prevSelectedSizes) =>
      prevSelectedSizes.includes(size)
        ? prevSelectedSizes.filter((s) => s !== size)
        : [...prevSelectedSizes, size]
    );
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="mx-auto px-3 sm:px-6 lg:px-8 py-6">
      <ProductcategoryDrawer />

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Sidebar */}
        <div className="lg:w-1/4 w-full">
          <ProductSidebar
            priceOptions={priceOptions}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            sizeoptions={sizeoptions}
            selectedSizes={selectedSizes}
            handleSizeChange={handleSizeChange}
            handleResetFilters={handleResetFilters}
          />
        </div>

        {/* Product List */}
        <div className="lg:w-3/4 w-full uppercase">
          {/* Filtered Products Section */}
          <div className="mb-6">
            <p className="text-xl font-semibold text-gray-800 mb-2">
              Filtered Products
            </p>
            <p className="text-gray-600 text-sm sm:text-base">
              {`Showing ${indexOfFirstProduct + 1}–${Math.min(
                indexOfLastProduct,
                filteredProducts.length
              )} of ${filteredProducts.length} results for ${categoryname}`}
            </p>
          </div>

          {/* Filtered Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-10">
            {currentProducts.map((product) => (
              <Product key={product._id} product={product} />
            ))}
          </div>

          {/* Divider */}
          <hr className="my-10 border-gray-300" />

          {/* Suggested Products Section */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
            Explore Other Products
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 capitalize">
            {allProducts.map((product) => (
              <Product key={product._id} product={product} />
            ))}
          </div>
        </div>
      </div>

      {/* Go to Home Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 transition duration-200"
        >
          <FaHome />
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default ProductList;